import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { fetchCrmInstitutes } from '../../../services/crmInstituteService';
import { fetchUsers } from '../../../services/userService';
import { createCrmOpportunity } from '../../../services/crmOpportunityService';

const priorities = ['High', 'Medium', 'Low'];
const statuses = ['Negotiation', 'Won', 'Lost'];
const sources = ['Referral', 'Website', 'Event', 'Google']; // Add more sources if needed

const AddOpportunity: React.FC = () => {
  const [institutes, setInstitutes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    crm_institute_id: '',
    assigned_to: '',
    priority: '',
    status: '',
    source: '', // Added Source field
    expected_close_date: ''
  });

  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const instituteData = await fetchCrmInstitutes();
        const userData = await fetchUsers();
        setInstitutes(instituteData);
        setUsers(userData);
      } catch (err) {
        setApiMessage({ type: 'error', text: 'Failed to load data.' });
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setApiMessage(null);

    try {
      const response = await createCrmOpportunity(formData);

      if (response?.message) {
        setApiMessage({ type: 'success', text: response.message });
      } else {
        setApiMessage({
          type: 'success',
          text: 'Opportunity added successfully!'
        });
      }

      // Reset form after successful submission
      setFormData({
        crm_institute_id: '',
        assigned_to: '',
        priority: '',
        status: '',
        source: '',
        expected_close_date: ''
      });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        'Failed to create opportunity. Please try again.';
      setApiMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Card className="shadow p-4">
        <Card.Body>
          <Card.Title className="fw-bold">Add New Opportunity</Card.Title>

          {/* 🔹 API Response Messages */}
          {apiMessage && (
            <Alert
              variant={apiMessage.type === 'success' ? 'success' : 'danger'}
            >
              {apiMessage.text}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Institute</Form.Label>
              <Form.Select
                value={formData.crm_institute_id}
                onChange={e =>
                  setFormData({ ...formData, crm_institute_id: e.target.value })
                }
                required
              >
                <option value="">Select Institute</option>
                {institutes.map(inst => (
                  <option
                    key={inst.crm_institute_id}
                    value={inst.crm_institute_id}
                  >
                    {inst.institute_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Assigned To</Form.Label>
              <Form.Select
                value={formData.assigned_to}
                onChange={e =>
                  setFormData({ ...formData, assigned_to: e.target.value })
                }
                required
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.username}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={formData.priority}
                onChange={e =>
                  setFormData({ ...formData, priority: e.target.value })
                }
                required
              >
                <option value="">Select Priority</option>
                {priorities.map(priority => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={formData.status}
                onChange={e =>
                  setFormData({ ...formData, status: e.target.value })
                }
                required
              >
                <option value="">Select Status</option>
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Source</Form.Label>
              <Form.Select
                value={formData.source}
                onChange={e =>
                  setFormData({ ...formData, source: e.target.value })
                }
                required
              >
                <option value="">Select Source</option>
                {sources.map(source => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expected Close Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.expected_close_date}
                onChange={e =>
                  setFormData({
                    ...formData,
                    expected_close_date: e.target.value
                  })
                }
              />
            </Form.Group>

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Add Opportunity'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddOpportunity;
